import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Plus, Star, Trash2, FileText, Download, Eye } from "lucide-react";
import { toast } from "sonner";

const DISCIPLINES = ["civil", "structural", "architectural", "electrical", "drainage", "stormwater", "traffic", "landscape", "as-built", "other"];

export function DrawingsTab({ jobId, project }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showOnlyCurrent, setShowOnlyCurrent] = useState(false);
  const [filter, setFilter] = useState("");
  const [uploadOpen, setUploadOpen] = useState(false);

  const load = async () => {
    if (!jobId) return;
    setLoading(true);
    try {
      const { data } = await api.get("/pm/drawings", { params: { job_id: jobId } });
      setItems(data.items || []);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [jobId]);

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    return items.filter((d) => {
      if (showOnlyCurrent && !d.is_current) return false;
      if (!q) return true;
      return [d.name, d.sheet_number, d.discipline, d.revision].filter(Boolean).join(" ").toLowerCase().includes(q);
    });
  }, [items, filter, showOnlyCurrent]);

  // Group by sheet_number for stack visualisation
  const grouped = useMemo(() => {
    const m = new Map();
    filtered.forEach((d) => {
      const key = d.sheet_number || "(no sheet #)";
      if (!m.has(key)) m.set(key, []);
      m.get(key).push(d);
    });
    return Array.from(m.entries()).sort((a, b) => a[0].localeCompare(b[0]));
  }, [filtered]);

  const markCurrent = async (id) => {
    try {
      await api.patch(`/pm/drawings/${id}?mark_current=true`, {});
      toast.success("Marked as current");
      load();
    } catch (e) {
      toast.error("Failed to mark current");
    }
  };
  const del = async (id) => {
    if (!confirm("Delete (archive) this drawing?")) return;
    await api.delete(`/pm/drawings/${id}`);
    load();
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <Input placeholder="Filter by name, sheet, discipline…" value={filter} onChange={(e) => setFilter(e.target.value)} className="h-9 max-w-xs rounded-sm border-zinc-400 text-xs" data-testid="pm-draw-filter" />
        <label className="flex items-center gap-1.5 text-xs cursor-pointer">
          <input type="checkbox" checked={showOnlyCurrent} onChange={(e) => setShowOnlyCurrent(e.target.checked)} data-testid="pm-draw-current-only" />
          Current revisions only
        </label>
        <div className="flex-1" />
        <Button onClick={() => setUploadOpen(true)} className="rounded-sm bg-[#FF5F15] hover:bg-[#E65100] text-white uppercase tracking-wider text-xs font-bold" data-testid="pm-draw-upload">
          <Plus className="w-3.5 h-3.5 mr-2" /> Upload drawing
        </Button>
      </div>

      {!loading && filtered.length === 0 && (
        <div className="hard-card p-10 text-center">
          <FileText className="w-10 h-10 mx-auto text-zinc-300 mb-3" />
          <p className="text-sm text-zinc-500">No drawings for <b>{project?.title}</b> yet. Upload your first PDF or image.</p>
        </div>
      )}

      <div className="space-y-3">
        {grouped.map(([sheet, drawings]) => (
          <div key={sheet} className="hard-card p-4" data-testid={`pm-draw-group-${sheet}`}>
            <div className="flex items-center gap-2 mb-3">
              <span className="font-mono text-[11px] font-bold uppercase tracking-widest bg-[#09090B] text-white px-2 py-0.5">{sheet}</span>
              <span className="text-xs text-zinc-500">{drawings.length} revision{drawings.length === 1 ? "" : "s"}</span>
            </div>
            <div className="divide-y divide-zinc-100">
              {drawings.map((d) => <DrawingRow key={d.id} d={d} onMarkCurrent={markCurrent} onDelete={del} />)}
            </div>
          </div>
        ))}
      </div>

      {uploadOpen && <UploadDialog jobId={jobId} onClose={() => setUploadOpen(false)} onSaved={() => { setUploadOpen(false); load(); }} />}
    </div>
  );
}

function DrawingRow({ d, onMarkCurrent, onDelete }) {
  const isPdf = (d.content_type || "").includes("pdf");
  const viewUrl = `${process.env.REACT_APP_BACKEND_URL}/api/files/view?path=${encodeURIComponent(d.storage_path)}`;
  const dlUrl = `${viewUrl}&download=1`;
  return (
    <div className="py-2 flex items-center gap-3 text-sm" data-testid={`pm-draw-row-${d.id}`}>
      <div className={`w-2 h-12 ${d.is_current ? "bg-[#FF5F15]" : "bg-zinc-200"}`} />
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-bold truncate">{d.name}</span>
          {d.is_current && <span className="text-[9px] font-bold uppercase tracking-wider bg-[#FF5F15] text-white px-1.5 py-0.5">Current</span>}
          <span className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">Rev {d.revision || "A"}</span>
          {d.discipline && <span className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">· {d.discipline}</span>}
        </div>
        <div className="text-[11px] text-zinc-500 mt-0.5">
          Uploaded {new Date(d.uploaded_at).toLocaleDateString("en-NZ")} by {d.uploaded_by_name}
        </div>
      </div>
      <div className="flex items-center gap-1 shrink-0">
        <a href={`${viewUrl}&auth=${(localStorage.getItem("fl_token") || "")}`} target="_blank" rel="noreferrer" className="inline-flex items-center text-zinc-600 hover:text-[#FF5F15] p-1.5" title="Open" data-testid={`pm-draw-view-${d.id}`}>
          <Eye className="w-4 h-4" />
        </a>
        <a href={`${dlUrl}&auth=${(localStorage.getItem("fl_token") || "")}`} target="_blank" rel="noreferrer" className="inline-flex items-center text-zinc-600 hover:text-[#FF5F15] p-1.5" title="Download" data-testid={`pm-draw-dl-${d.id}`}>
          <Download className="w-4 h-4" />
        </a>
        {!d.is_current && (
          <Button size="sm" variant="outline" onClick={() => onMarkCurrent(d.id)} className="rounded-sm h-7 text-xs" title="Mark current" data-testid={`pm-draw-mark-${d.id}`}>
            <Star className="w-3 h-3 mr-1" /> Current
          </Button>
        )}
        <Button size="icon" variant="ghost" className="h-7 w-7 text-red-600" onClick={() => onDelete(d.id)} data-testid={`pm-draw-delete-${d.id}`}>
          <Trash2 className="w-3.5 h-3.5" />
        </Button>
      </div>
    </div>
  );
}

function UploadDialog({ jobId, onClose, onSaved }) {
  const [form, setForm] = useState({ name: "", sheet_number: "", discipline: "civil", revision: "A", mark_current: true });
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);

  const upload = async () => {
    if (!file) { toast.error("Pick a file first"); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("job_id", jobId);
      fd.append("file", file);
      Object.entries(form).forEach(([k, v]) => { if (v !== "" && v !== null && v !== undefined) fd.append(k, String(v)); });
      await api.post("/pm/drawings", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Drawing uploaded");
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="rounded-sm max-w-lg" data-testid="pm-draw-upload-dialog">
        <DialogHeader>
          <DialogTitle className="font-display uppercase tracking-tight text-2xl">Upload drawing</DialogTitle>
          <DialogDescription>PDF or image. Use sheet number + revision to keep multiple versions stacked.</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2">
            <Label className="label-tag">File *</Label>
            <Input type="file" accept="application/pdf,image/*" onChange={(e) => setFile(e.target.files?.[0] || null)} className="mt-2 rounded-sm border-zinc-400" data-testid="pm-draw-file" />
          </div>
          <div className="col-span-2">
            <Label className="label-tag">Name</Label>
            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-2 rounded-sm border-zinc-400" placeholder="defaults to filename" data-testid="pm-draw-name" />
          </div>
          <div>
            <Label className="label-tag">Sheet #</Label>
            <Input value={form.sheet_number} onChange={(e) => setForm({ ...form, sheet_number: e.target.value })} className="mt-2 rounded-sm border-zinc-400" placeholder="e.g. C-101" data-testid="pm-draw-sheet" />
          </div>
          <div>
            <Label className="label-tag">Revision</Label>
            <Input value={form.revision} onChange={(e) => setForm({ ...form, revision: e.target.value })} className="mt-2 rounded-sm border-zinc-400" data-testid="pm-draw-rev" />
          </div>
          <div className="col-span-2">
            <Label className="label-tag">Discipline</Label>
            <select value={form.discipline} onChange={(e) => setForm({ ...form, discipline: e.target.value })} className="mt-2 w-full rounded-sm border border-zinc-400 px-3 py-2 text-sm bg-white" data-testid="pm-draw-discipline">
              {DISCIPLINES.map((d) => <option key={d} value={d}>{d}</option>)}
            </select>
          </div>
          <label className="col-span-2 flex items-center gap-2 text-sm cursor-pointer">
            <input type="checkbox" checked={form.mark_current} onChange={(e) => setForm({ ...form, mark_current: e.target.checked })} data-testid="pm-draw-current-flag" />
            Mark this revision as current for the sheet
          </label>
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose} className="rounded-sm" disabled={busy}>Cancel</Button>
          <Button onClick={upload} disabled={busy || !file} className="rounded-sm bg-[#FF5F15] hover:bg-[#E65100] text-white uppercase tracking-wider text-xs font-bold" data-testid="pm-draw-upload-submit">
            {busy ? "Uploading…" : "Upload"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
